/**
 * EntryBadge - Small badge shown in calendar cells
 */

import { motion } from 'framer-motion';
import {
  Check,
  RotateCcw,
  AlertTriangle,
  Briefcase,
  Home,
  CreditCard,
  Landmark,
  Tv,
  Receipt,
  ArrowRightLeft,
  TrendingUp,
  ShoppingBag,
  Utensils,
  Car,
  Heart,
  Gamepad2,
  MoreHorizontal,
} from 'lucide-react';
import { useCashflowStore } from '../../stores/cashflowStore';

// Category icons mapping
const CATEGORY_ICONS = {
  salary: Briefcase,
  rent: Home,
  credit_card: CreditCard,
  loan: Landmark,
  subscription: Tv,
  bill: Receipt,
  transfer: ArrowRightLeft,
  investment: TrendingUp,
  shopping: ShoppingBag,
  food: Utensils,
  transport: Car,
  health: Heart,
  entertainment: Gamepad2,
  other: MoreHorizontal,
};

// Format currency compactly
const formatCompact = (amount, currency = 'TRY') => {
  const symbols = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || currency;

  if (amount >= 1000000) {
    return `${symbol}${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 100000) {
    // 100K+ shows without decimal
    return `${symbol}${Math.round(amount / 1000)}K`;
  }
  if (amount >= 10000) {
    // 10K-99K shows without decimal
    return `${symbol}${Math.round(amount / 1000)}K`;
  }
  // Under 10K shows with thousand separator
  return `${symbol}${amount.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
};

const EntryBadge = ({ entry, onClick }) => {
  const { toggleCompleted } = useCashflowStore();

  const isIncome = entry.type === 'income';
  const isCompleted = entry.status === 'completed';
  const isOverdue = entry.status === 'overdue';
  const isRecurring = entry.is_recurring || entry.recurring_parent_id;
  const isVirtual = entry.is_virtual;

  // Color classes based on type and status
  const getColorClasses = () => {
    if (isCompleted) {
      return {
        bg: 'bg-gray-800/50',
        border: 'border-gray-700',
        text: 'text-gray-500',
        amount: 'text-gray-500 line-through',
      };
    }
    if (isOverdue) {
      return {
        bg: 'bg-amber-900/30',
        border: 'border-amber-500/50',
        text: 'text-amber-400',
        amount: 'text-amber-400',
      };
    }
    if (isIncome) {
      return {
        bg: 'bg-emerald-900/30',
        border: 'border-emerald-700/50',
        text: 'text-gray-300',
        amount: 'text-emerald-400',
      };
    }
    return {
      bg: 'bg-red-900/30',
      border: 'border-red-700/50',
      text: 'text-gray-300',
      amount: 'text-red-400',
    };
  };

  const colors = getColorClasses();

  const handleCheckClick = (e) => {
    e.stopPropagation();
    toggleCompleted(entry);
  };

  return (
    <motion.div
      className={`group relative ${colors.bg} border ${colors.border} rounded-md px-2 py-1 cursor-pointer transition-all hover:scale-[1.02]`}
      onClick={onClick}
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-center justify-between gap-1">
        {/* Amount */}
        <span className={`text-xs font-medium ${colors.amount} truncate`}>
          {isIncome ? '+' : '-'}
          {formatCompact(entry.amount, entry.currency)}
        </span>

        {/* Status indicators */}
        <div className="flex items-center gap-1">
          {/* Recurring indicator */}
          {isRecurring && (
            <RotateCcw className="w-3 h-3 text-blue-400" />
          )}

          {/* Overdue indicator */}
          {isOverdue && (
            <AlertTriangle className="w-3 h-3 text-amber-400" />
          )}

          {/* Checkbox */}
          {!isVirtual && (
            <button
              onClick={handleCheckClick}
              className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                isCompleted
                  ? 'bg-emerald-500 border-emerald-500'
                  : 'border-gray-600 hover:border-gray-400 group-hover:opacity-100 opacity-0'
              }`}
            >
              {isCompleted && <Check className="w-3 h-3 text-white" />}
            </button>
          )}

          {/* Virtual entry indicator */}
          {isVirtual && (
            <span className="w-4 h-4 rounded border border-dashed border-gray-600 flex items-center justify-center opacity-50">
              <RotateCcw className="w-2.5 h-2.5 text-gray-500" />
            </span>
          )}
        </div>
      </div>

      {/* Title with category icon */}
      <div className="flex items-center gap-1">
        {(() => {
          const CategoryIcon = CATEGORY_ICONS[entry.category] || MoreHorizontal;
          return <CategoryIcon className={`w-3 h-3 ${colors.text} flex-shrink-0 opacity-70`} />;
        })()}
        <p className={`text-[10px] ${colors.text} truncate`}>
          {entry.title}
        </p>
      </div>
    </motion.div>
  );
};

export default EntryBadge;
